import React, {useCallback, useState, type JSX} from 'react';
import {
  Box,
  Button,
  ClickAwayListener,
  Fade,
  List,
  ListItemButton,
  ListItemText,
  Paper,
} from '@mui/material';
import {styled} from '@mui/material/styles';
import {ChevronUpDownIcon} from '../../../../assets/icons/chevronUpDownIcon';
import {type DateListType} from '../constants/types';

const StyledDateSelectButton = styled(Button)(({theme}) => ({
  '&.MuiButton-root': {
    height: '36px',
    width: '100%',
    border: `1px solid ${theme.palette.grey[400]}`,
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '5px',
  },
}));

const MenuListContainer = styled('div')({
  position: 'absolute',
  width: 'inherit',
});

const StyledListItem = styled(ListItemButton)({
  paddingBottom: 0,
  paddingTop: 0,
});

const StyledList = styled(List)({
  maxHeight: '350px',
  overflowY: 'auto',
});

type Props = {
  selectedValue: string;
  handleChange: (value: number) => void;
  items: DateListType;
};

export function DateSelect({
  selectedValue,
  items,
  handleChange,
}: Readonly<Props>): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);

  const handleClickAway = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onClick = useCallback(() => {
    setIsOpen(previousState => !previousState);
  }, []);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box style={{width: 'inherit'}}>
        <StyledDateSelectButton
          endIcon={<ChevronUpDownIcon />}
          onClick={onClick}
          variant="outlined"
        >
          {selectedValue}
        </StyledDateSelectButton>
        <Fade in={isOpen}>
          <MenuListContainer>
            <Paper>
              <StyledList>
                {Object.keys(items).map((item: string) => (
                  <ListItemFun
                    key={item}
                    item={item}
                    items={items}
                    selectedValue={selectedValue}
                    setIsOpen={setIsOpen}
                    handleChange={handleChange}
                  />
                ))}
              </StyledList>
            </Paper>
          </MenuListContainer>
        </Fade>
      </Box>
    </ClickAwayListener>
  );
}

type ListItemFunProps = {
  item: string;
  items: DateListType;
  selectedValue: string;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleChange: (value: number) => void;
};

function ListItemFun({
  item,
  items,
  selectedValue,
  setIsOpen,
  handleChange,
}: Readonly<ListItemFunProps>): JSX.Element {
  const onClick = useCallback(() => {
    setIsOpen(false);
    handleChange(Number(item));
  }, [handleChange, item, setIsOpen]);

  return (
    <StyledListItem
      key={item}
      selected={items[Number(item)] === selectedValue}
      onClick={onClick}
    >
      <ListItemText primary={items[Number(item)]} />
    </StyledListItem>
  );
}
